import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Building2, MapPin, Image as ImageIcon, FileText, MessageSquare, ArrowLeft } from 'lucide-react';
import { PROPERTY_TYPE_LABEL } from '@/lib/constants';
import { NotesPanel } from '@/components/app/NotesPanel';
import { PropertyMedia } from '@/components/app/PropertyMedia';

export default function AgentPropertyDetail() {
  const { id } = useParams();
  const [prop, setProp] = useState(null);
  const [units, setUnits] = useState([]);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => setProp(r.data)).catch(() => {});
    api.get('/units', { params: { property_id: id } }).then((r) => setUnits(r.data)).catch(() => {});
  }, [id]);

  if (!prop) return <div className="text-sm text-muted-foreground py-12 text-center">Loading property…</div>;
  const vacant = units.filter((u) => u.status === 'vacant');
  const occupied = units.filter((u) => u.status === 'occupied');

  return (
    <div className="space-y-4">
      <Link to="/agent/properties" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Back to properties</Link>
      <PageHeader title={prop.name} subtitle={`${PROPERTY_TYPE_LABEL[prop.type] || prop.type} · ${prop.city || ''}`} />

      <Card className="overflow-hidden">
        {prop.image_url && <div className="h-48 sm:h-64 bg-muted"><img src={prop.image_url} className="h-full w-full object-cover" alt="" /></div>}
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          <div><div className="text-xs text-muted-foreground">Code</div><div className="font-mono">{prop.code}</div></div>
          <div><div className="text-xs text-muted-foreground">Type</div><Badge variant="outline" className="capitalize">{PROPERTY_TYPE_LABEL[prop.type] || prop.type}</Badge></div>
          <div><div className="text-xs text-muted-foreground">Total units</div><div className="font-medium">{prop.total_units}</div></div>
          <div><div className="text-xs text-muted-foreground">Status</div><div className="capitalize font-medium">{prop.status}</div></div>
          <div className="sm:col-span-2 lg:col-span-4"><div className="text-xs text-muted-foreground">Address</div><div className="flex items-start gap-1"><MapPin className="h-3.5 w-3.5 mt-0.5 text-muted-foreground" /> {prop.address}</div></div>
        </div>
      </Card>

      <Tabs defaultValue="units">
        <TabsList>
          <TabsTrigger value="units"><Building2 className="h-3.5 w-3.5 mr-1" /> Units &amp; Availability</TabsTrigger>
          <TabsTrigger value="media"><ImageIcon className="h-3.5 w-3.5 mr-1" /> Media</TabsTrigger>
          <TabsTrigger value="notes"><MessageSquare className="h-3.5 w-3.5 mr-1" /> Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="units" className="mt-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {units.length === 0 && <div className="col-span-full text-sm text-center text-muted-foreground py-8">No units listed yet.</div>}
            {units.map((u) => (
              <Card key={u.id} className="p-4 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="font-semibold">Unit {u.unit_number}</div>
                  <Badge variant="outline" className={u.status === 'vacant' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : u.status === 'occupied' ? 'bg-slate-100 text-slate-700' : 'bg-amber-50 text-amber-700 border-amber-200'}>
                    {u.status}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground">{u.configuration} · {u.area_sqft} sqft · Floor {u.floor || '—'}</div>
                {/* Agent does NOT see rent/deposit financials */}
              </Card>
            ))}
          </div>
          <div className="mt-3 text-xs text-muted-foreground">
            Showing {vacant.length} vacant · {occupied.length} occupied. Rent &amp; deposit amounts are visible only to the owner and the property consultant.
          </div>
        </TabsContent>

        <TabsContent value="media" className="mt-3">
          <PropertyMedia propertyId={prop.id} canManage={false} />
        </TabsContent>

        <TabsContent value="notes" className="mt-3">
          <NotesPanel propertyId={prop.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
